/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        QA Linter & Automated Translation Quality DTOs (Task 8.4.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from 'zod';

export const QaSeverityEnum = z.enum(['error', 'warning']);
export type QaSeverity = z.infer<typeof QaSeverityEnum>;

export const QaRuleIdEnum = z.enum([
  'punctuation_mismatch',
  'number_inconsistency',
  'missing_placeholder',
  'spurious_placeholder',
  'broken_tag',
  'length_anomaly',
  'capitalization',
  'double_space',
  'trailing_space',
]);
export type QaRuleId = z.infer<typeof QaRuleIdEnum>;

export const QaIssueSchema = z.object({
  ruleId: QaRuleIdEnum.openapi({
    description: 'Identifier of the triggered QA inspection rule',
    example: 'punctuation_mismatch',
  }),
  severity: QaSeverityEnum.openapi({
    description: 'Severity level (error blocks approval, warning suggests review)',
    example: 'warning',
  }),
  message: z.string().openapi({
    description: 'Human-readable explanation of the detected defect',
    example: "Punctuation mismatch: source ends with '?', but target does not",
  }),
  expected: z.string().optional().openapi({
    description: 'Expected character, placeholder, or token',
    example: '?',
  }),
  actual: z.string().optional().openapi({
    description: 'Actual character, placeholder, or token found in translation',
    example: '.',
  }),
}).openapi({
  title: 'QaIssue',
  description: 'Individual translation QA issue or warning',
});
export type QaIssueDto = z.infer<typeof QaIssueSchema>;

export const QaLintResultSchema = z.object({
  isValid: z.boolean().openapi({
    description: 'True if there are zero error-severity issues',
    example: true,
  }),
  errorCount: z.number().int().min(0).openapi({
    description: 'Total number of blocking error issues',
    example: 0,
  }),
  warningCount: z.number().int().min(0).openapi({
    description: 'Total number of advisory warnings',
    example: 1,
  }),
  issues: z.array(QaIssueSchema).openapi({
    description: 'List of structured QA issues detected',
  }),
  warnings: z.array(z.string()).openapi({
    description: 'List of human-readable warning strings for backward compatibility',
    example: ["Punctuation mismatch: source ends with '?', but target does not"],
  }),
}).openapi({
  title: 'QaLintResult',
  description: 'QA inspection result for a single segment',
});
export type QaLintResultDto = z.infer<typeof QaLintResultSchema>;

export const QaCheckRequestSchema = z.object({
  sourceText: z.string().min(1).openapi({
    description: 'Original source segment text',
    example: 'Welcome to <b>Sophia Platform</b>! Enjoy your 100 credits.',
  }),
  targetText: z.string().nullable().optional().openapi({
    description: 'Target translated segment text',
    example: 'Chào mừng bạn đến với <b>Sophia Platform</b>! Tận hưởng 100 tín chỉ của bạn.',
  }),
  sourceLang: z.string().max(10).optional().openapi({
    description: 'Source language code (e.g. en)',
    example: 'en',
  }),
  targetLang: z.string().max(10).optional().openapi({
    description: 'Target language code (e.g. vi)',
    example: 'vi',
  }),
}).openapi({
  title: 'QaCheckRequest',
  description: 'Payload for on-the-fly QA check of a bilingual segment pair',
});
export type QaCheckRequestDto = z.infer<typeof QaCheckRequestSchema>;

export const QaCheckResponseSchema = z.object({
  success: z.boolean().openapi({ example: true }),
  data: QaLintResultSchema,
}).openapi({
  title: 'QaCheckResponse',
});
export type QaCheckResponseDto = z.infer<typeof QaCheckResponseSchema>;

export const ProjectQaReportItemSchema = z.object({
  chunkId: z.string().openapi({ example: 'chk_123456789' }),
  sequenceOrder: z.number().int().min(0).openapi({ example: 0 }),
  sourceText: z.string().openapi({ example: 'Are you ready?' }),
  targetText: z.string().nullable().openapi({ example: 'Bạn đã sẵn sàng.' }),
  issues: z.array(QaIssueSchema),
  warnings: z.array(z.string()),
}).openapi({
  title: 'ProjectQaReportItem',
  description: 'Segment with QA issues in the project inspection report',
});
export type ProjectQaReportItemDto = z.infer<typeof ProjectQaReportItemSchema>;

export const ProjectQaReportRequestSchema = z.object({
  chunkIds: z.array(z.string()).optional().openapi({
    description: 'Optional subset of chunk IDs to inspect. If omitted, all segments in the project are scanned.',
  }),
  onlyErrors: z.boolean().optional().default(false).openapi({
    description: 'If true, only returns items that contain error-severity defects',
    example: false,
  }),
}).openapi({
  title: 'ProjectQaReportRequest',
  description: 'Options for scanning project chunks for QA defects',
});
export type ProjectQaReportRequestDto = z.infer<typeof ProjectQaReportRequestSchema>;

export const ProjectQaReportResponseSchema = z.object({
  success: z.boolean().openapi({ example: true }),
  data: z.object({
    translationId: z.string().openapi({ example: 'proj_cat_001' }),
    totalChunks: z.number().int().min(0).openapi({ example: 120 }),
    reviewedChunks: z.number().int().min(0).openapi({ example: 95 }),
    unreviewedChunks: z.number().int().min(0).openapi({ example: 25 }),
    chunksWithIssues: z.number().int().min(0).openapi({ example: 4 }),
    totalErrors: z.number().int().min(0).openapi({ example: 1 }),
    totalWarnings: z.number().int().min(0).openapi({ example: 5 }),
    items: z.array(ProjectQaReportItemSchema),
  }),
}).openapi({
  title: 'ProjectQaReportResponse',
  description: 'Consolidated QA report for the translation project',
});
export type ProjectQaReportResponseDto = z.infer<typeof ProjectQaReportResponseSchema>;
